import React, { useState, useEffect } from 'react';
import Icon from '@components/AppIcon';
import Image from '@components/AppImage';

const TestimonialCarousel = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch home page data from API and extract testimonials
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await fetch('/api/pages/home');
        if (!response.ok) {
          throw new Error('Failed to fetch home page data');
        }
        const homeData = await response.json();
        
        // Extract testimonials from home page data
        let extractedTestimonials = [];
        
        if (homeData.homepage_sections && Array.isArray(homeData.homepage_sections)) {
  // Find ALL testimonial sections
  const testimonialSections = homeData.homepage_sections.filter(
    section => section.type === 'testimonials' && (section.enabled ?? true)
  );
  
  // Extract testimonials from all sections
  testimonialSections.forEach(section => {
    if (section.testimonials && Array.isArray(section.testimonials)) {
      const sectionTestimonials = section.testimonials
        .filter(testimonial => testimonial.enabled ?? true)
        .map(testimonial => ({
          id: testimonial.id || Math.random().toString(36).substr(2, 9),
          name: testimonial.patient_name || '',
          youtube_id: testimonial.youtube_id || null,
          patient_quote: testimonial.patient_quote || '',
          posting_date: testimonial.posting_date || '',
          rating: 5 // Default rating
        }));
      
      extractedTestimonials = [...extractedTestimonials, ...sectionTestimonials];
    }
  });
}
        
        setTestimonials(extractedTestimonials);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  // Auto-rotate testimonials when not playing
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPlaying && testimonials.length > 0) {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const playVideo = () => {
    setIsPlaying(true);
  };

  const stopVideo = () => {
    setIsPlaying(false);
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>Loading testimonials...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>Error loading testimonials: {error}</p>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null; // Don't render anything if no testimonials
  }

  const current = testimonials[currentTestimonial];

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4" style={{ color: "#08005f" }}>
            Real Stories, Real Transformations
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Hear from thousands of Kenyans who've trusted Eye&U with their vision. 
            Their success stories speak louder than any advertisement.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Video/Image Section */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl shadow-lg">
              {current.youtube_id ? (
                isPlaying ? (
                  // YouTube Video Player
                  <div className="w-full h-[400px]">
                    <iframe
                      src={`https://www.youtube.com/embed/${current.youtube_id}?autoplay=1`}
                      title={`${current.name} testimonial video`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                    <button
                      onClick={stopVideo}
                      className="absolute top-4 right-4 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition"
                      aria-label="Stop video"
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>
                ) : (
                  // YouTube Thumbnail with Play Button
                  <>
                    <Image
                      src={`https://img.youtube.com/vi/${current.youtube_id}/maxresdefault.jpg`}
                      alt={`${current.name} testimonial video`}
                      className="w-full h-[400px] object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=400';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <button
                        onClick={playVideo}
                        className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition hover:scale-110"
                        aria-label="Play testimonial video"
                      >
                        <Icon name="Play" size={32} className="text-[#eb2227]" />
                      </button>
                    </div>
                  </>
                )
              ) : (
                // Default Image for Text-Only Testimonials
                <div className="w-full h-[400px] bg-gradient-to-br from-[#08005f] to-[#08005f] flex items-center justify-center">
                  <Icon name="Quote" size={64} className="text-white opacity-80" />
                </div>
              )}

              {/* Navigation Controls */}
              <div className="absolute top-1/2 -translate-y-1/2 left-4 right-4 flex justify-between">
                <button
                  onClick={prevTestimonial}
                  className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition"
                  aria-label="Previous testimonial"
                >
                  <Icon name="ChevronLeft" size={20} className="text-gray-800" />
                </button>
                
                <button
                  onClick={nextTestimonial}
                  className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition"
                  aria-label="Next testimonial"
                >
                  <Icon name="ChevronRight" size={20} className="text-gray-800" />
                </button>
              </div>
            </div>
          </div>

          {/* Testimonial Content */}
          <div className="space-y-6">
            {/* Patient Info */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#08005f] to-[#08005f] flex items-center justify-center">
                  <Icon name="User" size={24} className="text-white" />
                </div>
              </div>
              
              <div>
                <h3 className="font-heading font-bold text-text-primary">
                  {current.name}
                </h3>
                {current.posting_date && (
                  <div className="text-sm text-text-secondary">
                    Posted on {new Date(current.posting_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Rating */}
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                {[...Array(current.rating)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} color="#f59e0b" fill="#f59e0b" />
                ))}
              </div>
              <span className="text-sm text-text-secondary">
                {current.rating}.0 out of 5 stars
              </span>
            </div>

            {/* Testimonial Text */}
            {current.patient_quote && (
              <blockquote className="text-lg text-text-primary leading-relaxed">
                "{current.patient_quote}"
              </blockquote>
            )}

            {/* Verification Badge */}
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="Shield" size={16} className="text-[#08005f]" />
              <span>Verified Patient • Treated at Eye&U Kenya</span>
            </div>

            {/* Video Indicator */}
            {current.youtube_id && (
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Video" size={16} className="text-[#eb2227]" />
                <span>Video testimonial available</span>
              </div>
            )}
          </div>
        </div>

        {/* Testimonial Indicators */}
        <div className="flex justify-center mt-8 space-x-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentTestimonial(index)}
              className={`w-3 h-3 rounded-full transition ${
                index === currentTestimonial ? 'bg-[#08005f]' : 'bg-gray-300'
              }`}
              aria-label={`View testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;